// REACT IMPORTS
import React, { useEffect, useState } from "react";

// COMPONENT IMPORTS
import Header from "components/layout/header";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  cardDeleteOrDefault,
  countryListGet,
  oldPaymentMethodsGet,
  updateBankList,
  updateCardList,
  updateWalletList,
  useBankData,
  useCardData,
  useOldWalletData,
  walletsProfile,
} from "store/slices/paymentMethodSlice";
import { useUserData } from "store/slices/authSlice";

// ICONS IMPORTS
import Skeleton from "react-loading-skeleton";
import { AddCirclePrimaryIcon } from "icons/payments/AddCirclePrimaryIcon";
import PaymentMethodCard from "components/paymentMethod/PaymentMethodCard";
import PaymentMethodBankCard from "components/paymentMethod/PaymentMethodBankCard";
import PaymentMethodWalletCard from "components/paymentMethod/PaymentMethodWalletCard";
import PaymentMethodDialogs from "components/paymentMethod/PaymentMethodDialogs";
import { leadData, useLeadData } from "store/slices/dashboardSlice";
import {
  decode,
  encode,
  maskString,
  shortToCardName,
} from "utils/HelperFunctions";

// FUNCTIONS IMPORT
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import {
  createPaypalPaymentToken,
  getpaymentPermission,
} from "store/slices/paymentsSlice";
import {
  ModalItem,
  PaymentMethodTypes,
  WalletModal,
} from "types/paymentMethods";
import { RootState } from "store/store";
import { useIsLogin } from "store/slices/commonSlice";
import { PaymentGateway, SettingsValue } from "types/payments";
import { TestIdScope, useTid } from "utils/testid";
import { EDIT_CARD_DRAWER } from "utils/updateCardRoute";

/* -------------------------------------PAYMENT METHOD ------------------------------------- */
declare global {
  interface Window {
    StaxJs: any;
  }
}

const getPaymentMethodKey = (value: any, fallback: number | string) =>
  value?.id || value?.payment_card_id || value?.account_number || fallback;

const PaymentMethodContent: React.FC = () => {
  const tid = useTid();
  const dispatch = useAppDispatch();
  const isLessThan590 = useMediaQuery({ query: "(max-width:590px)" });
  // The redesigned 350x200 tile applies below xl; desktop keeps 260x150.
  const isDesktopTile = useMediaQuery({ query: "(min-width:1025px)" });
  const tileW = isDesktopTile ? 260 : 350;
  const tileH = isDesktopTile ? 150 : 200;
  const oldCardData = useCardData() as PaymentMethodTypes[];
  const oldBankData = useBankData() as PaymentMethodTypes[];
  const walletList = useOldWalletData() as PaymentMethodTypes[];
  const userData = useUserData();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isBtnLoading, setBtnLoading] = useState<boolean>(false);
  const [isAddCard, setIsAddCard] = useState<boolean>(false);
  const [isAddBank, setIsAddBank] = useState<boolean>(false);
  const [isAddWallet, setIsAddWallet] = useState<boolean>(false);
  const [isEditCard, setIsEditCard] = useState<boolean>(false);
  const [isEditBank, setIsEditBank] = useState<boolean>(false);
  const [isEditWallet, setIsEditWallet] = useState<boolean>(false);
  const [editBankData, setEditBankData] = useState<PaymentMethodTypes | null>(
    null
  );
  const [editCardData, setEditCardData] = useState<
    string | PaymentMethodTypes | null
  >(null);
  const [editWalletData, setEditWalletData] =
    useState<PaymentMethodTypes | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const accountLeadData = useLeadData();
  const [showLimits, setShowLimits] = useState<number>(3);
  const [limitCards, setLimitCards] = useState<number>(showLimits);
  const [limitBanks, setLimitBanks] = useState<number>(showLimits);
  const [limitWallets, setLimitWallets] = useState<number>(showLimits);
  const [isSuccessScreeenWallet, setIsSuccessScreenWallet] =
    useState<boolean>(false);
  const [isFailedScreenWallet, setIsFailedScreenWallet] =
    useState<boolean>(false);
  const [isScreenLoading, setIsScreenLoading] = useState<boolean>(false);
  const [hasLoadedCards, setHasLoadedCards] = useState<boolean>(false);
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const { payPalOrder, paymentPermission } = useAppSelector(
    (state: RootState) => state.payment
  );
  const [hasLoadedPaymentPermission, setHasLoadedPaymentPermission] =
    useState(false);
  const [isPaymentPermissionLoading, setPaymentPermissionLoading] =
    useState(true);
  const isCardAvailable =
    hasLoadedPaymentPermission &&
    paymentPermission?.cc_ext_availability === SettingsValue.YES;
  const isBankAvailable =
    hasLoadedPaymentPermission &&
    paymentPermission?.ach_ext_availability === SettingsValue.YES;
  const [cardNumber, setCardNumber] = useState<ModalItem>({
    number: null,
    id: null,
    showModal: false,
  });
  const [deleteCard, setDeleteCard] = useState<ModalItem>({
    number: null,
    id: null,
    showModal: false,
  });
  const [wallet, setWallet] = useState<WalletModal>({
    method: null,
    id: null,
    showModal: false,
    type: null,
  });
  const isLogin = useIsLogin();

  const onOldCardDataGet = async () => {
    setLoading(true);
    try {
      let payload = {
        accountid: userData?.data?.accountData?.accountid,
        accounts_id: userData?.data?.accountData?.id,
        cluster_id: clusterId,
      };
      await dispatch(oldPaymentMethodsGet(payload)).unwrap();
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      console.log("old card data get erro:-->", error?.message);
    }
    setHasLoadedCards(true);
  };

  const openEditCard = (cardId: number | string) => {
    let slug = encode(`${cardId}_${clusterId}_${new Date().getTime()}`, "pm_");
    setIsAddCard(true);
    setIsEditCard(true);
    setEditCardData(slug);
  };

  const onCardDelete = async () => {
    setBtnLoading(true);
    try {
      let payload = {
        isDelete: true,
        isDefault: false,
        payment_card_id: deleteCard?.id,
        accounts_id: userData?.data?.accountData?.id,
      };
      await dispatch(cardDeleteOrDefault(payload)).unwrap();
      setDeleteCard((current) => ({ ...current, showModal: false }));
      onOldCardDataGet();
      setBtnLoading(false);
    } catch (error: any) {
      setBtnLoading(false);
      console.log("delete card err: -->", error?.message);
    }
  };

  const onCardDefault = async () => {
    const bank = oldBankData?.find((item: any) => item.id === cardNumber?.id);
    if (bank) {
      const unlinkedStatus =
        bank?.is_connected === "N" &&
        (bank?.gateway_name === PaymentGateway?.STRIPE || bank?.pc_id > 0);
      if (unlinkedStatus) {
        setCardNumber((current) => ({ ...current, showModal: false }));
        return;
      }
    }

    setBtnLoading(true);
    try {
      let payload = {
        isDelete: false,
        isDefault: true,
        payment_card_id: cardNumber?.id,
        accounts_id: userData?.data?.accountData?.id,
      };
      await dispatch(cardDeleteOrDefault(payload)).unwrap();
      setCardNumber((current) => ({ ...current, showModal: false }));
      onOldCardDataGet();
      setBtnLoading(false);
    } catch (error: any) {
      setBtnLoading(false);
      console.log("default card err: -->", error?.message);
    }
  };

  const onCountryListGet = async () => {
    try {
      await dispatch(countryListGet()).unwrap();
    } catch (error: any) {
      console.log("conntry list get err: -->", error?.message);
    }
  };

  const onGetLeadData = async () => {
    let lead_payload: any = {
      accounts_id: userData.data.accountData.id,
      cluster_id: userData.data.accountData.clusterid,
      accountid: userData.data.accountData.accountid,
    };
    try {
      await dispatch(leadData(lead_payload)).unwrap();
    } catch (error: any) {
      console.log("onGetLeadData error", error);
    }
  };

  const handleUpdateWallet = async () => {
    try {
      setBtnLoading(true);
      let payload = {
        isDelete: wallet.type === "delete" ? true : false,
        isDefault: wallet.type !== "delete" ? true : false,
        payment_card_id: wallet.id,
        accounts_id: userData?.data?.accountData?.id,
      };
      let res: any = await dispatch(cardDeleteOrDefault(payload)).unwrap();

      if (res.body.success) {
        if (wallet.type === "delete") {
          let formattedWalletList = walletList.filter(
            (item: any) => item.id !== wallet.id
          );
          await dispatch(updateWalletList(formattedWalletList));
        } else {
          let formattedWalletList = walletList.map((item: any) => {
            if (item.id === wallet.id) {
              return { ...item, default_profile: "Y" };
            } else {
              return { ...item, default_profile: "N" };
            }
          });

          // updateCardList,updateBankList
          let formattedCardList = oldCardData.map((item: any) => ({
            ...item,
            default_profile: "N",
          }));
          let formattedBankList = oldBankData.map((item: any) => ({
            ...item,
            default_profile: "N",
          }));
          await dispatch(updateCardList(formattedCardList));
          await dispatch(updateBankList(formattedBankList));
          await dispatch(updateWalletList(formattedWalletList));
        }
      }
      setBtnLoading(false);
      setWallet((current) => ({ ...current, showModal: false }));
    } catch (error) {
      console.log("handleUpdateWallet error =====", error);
    }
  };

  useEffect(() => {
    onOldCardDataGet();
    onGetLeadData();
    onCountryListGet();
  }, []);

  // An update-card link lands here after login. Open the edit drawer only for
  // a card in this customer's own list; any other id just shows the page.
  useEffect(() => {
    if (searchParams.get("drawer") !== EDIT_CARD_DRAWER) return;
    if (!hasLoadedCards || !hasLoadedPaymentPermission) return;

    const cardId = searchParams.get("cardId");
    const card = oldCardData?.find((item) => String(item?.id) === cardId);
    if (card && isCardAvailable) {
      openEditCard(card.id);
    }

    const nextSearch = new URLSearchParams(location.search);
    nextSearch.delete("drawer");
    nextSearch.delete("cardId");
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch.toString() ? `?${nextSearch.toString()}` : "",
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLoadedCards, hasLoadedPaymentPermission, location.search]);

  useEffect(() => {
    if (!hasLoadedPaymentPermission) return;

    const createWalletsProfile = async (payment_method: string) => {
      try {
        let setupIntent = searchParams.get("setup_intent");
        let payload = {
          lead_id: accountLeadData?.lead_id,
          cluster_id: userData.data.accountData.clusterid,
          accountid: userData.data.accountData.accountid,
          module: "process_payment",
          setupIntent: setupIntent,
          x_method: payment_method,
          email: userData?.data?.accountData?.email,
        };

        const response = await dispatch(walletsProfile(payload));

        if (response?.meta?.requestStatus === "fulfilled") {
          await onOldCardDataGet();
          setIsSuccessScreenWallet(true);
        } else if (response?.meta?.requestStatus === "rejected") {
          setIsFailedScreenWallet(true);
        }
      } catch (error) {
        console.error("createWalletsProfile error =====", error);
      } finally {
        // setIsSuccessScreenWallet(true);
        setIsScreenLoading(false);
      }
    };

    const createPayPalProfile = async (tokendata: { token: string }) => {
      try {
        await dispatch(createPaypalPaymentToken(tokendata));
        setIsAddWallet(true);
      } catch (error) {
      } finally {
        setIsScreenLoading(false);
      }
    };

    const editBankOrCard = async (
      payment_method: string,
      isEdit: boolean,
      cppId: number
    ) => {
      try {
        let setupIntent = searchParams.get("setup_intent");
        let payload = {
          lead_id: accountLeadData?.lead_id,
          cluster_id: userData.data.accountData.clusterid,
          accountid: userData.data.accountData.accountid,
          module: "process_payment",
          setupIntent: setupIntent,
          x_method: payment_method,
          isEdit,
          cppid: cppId,
        };
        await dispatch(walletsProfile(payload));
        await onOldCardDataGet();
      } catch (error) {
        console.log("createWalletsProfile error =====", error);
      } finally {
        setIsSuccessScreenWallet(true);
        setIsScreenLoading(false);
      }
    };

    if (
      searchParams.has("setup_intent") &&
      searchParams.has("checked") &&
      searchParams.get("checked") === "false" &&
      searchParams.has("payment_method") &&
      searchParams.get("payment_method") === "CAPP"
    ) {
      searchParams.set("checked", "true");
      setIsScreenLoading(true);
      setIsAddWallet(true);
      createWalletsProfile("CAPP");
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }

    if (
      searchParams.has("setup_intent") &&
      searchParams.has("checked") &&
      searchParams.get("checked") === "false" &&
      searchParams.has("payment_method") &&
      searchParams.get("payment_method") === "AMZP"
    ) {
      searchParams.set("checked", "true");
      setIsScreenLoading(true);
      setIsAddWallet(true);
      createWalletsProfile("AMZP");
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }
    if (
      (searchParams.has("approval_session_id") ||
        searchParams.has("approval_token_id")) &&
      !searchParams.has("checked") &&
      searchParams.has("status") &&
      searchParams.get("method") === "paypal"
    ) {
      searchParams.set("checked", "true");
      setIsScreenLoading(true);
      const tokendata = {
        token: payPalOrder?.token,
      };
      createPayPalProfile(tokendata);

      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );

        navigate({ search: "" });
      }, 500);
    }

    if (
      searchParams.has("setup_intent") &&
      searchParams.has("checked") &&
      searchParams.get("checked") === "false" &&
      searchParams.has("payment_method") &&
      searchParams.get("payment_method") === "ACH" &&
      isBankAvailable
    ) {
      searchParams.set("checked", "true");
      let isEdit = searchParams.has("isEdit")
        ? searchParams.get("isEdit") === "true"
        : false;
      let cppId = searchParams.has("cppId")
        ? Number(searchParams.get("cppId"))
        : 0;
      if (isEdit) {
        editBankOrCard("ACH", isEdit, cppId);
      } else {
        createWalletsProfile("ACH");
      }
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }

    if (
      searchParams.has("setup_intent") &&
      searchParams.has("checked") &&
      searchParams.get("checked") === "false" &&
      searchParams.has("payment_method") &&
      searchParams.get("payment_method") === "CC" &&
      isCardAvailable
    ) {
      searchParams.set("checked", "true");
      let isEdit = searchParams.has("isEdit")
        ? searchParams.get("isEdit") === "true"
        : false;
      let cppId = searchParams.has("cppId")
        ? Number(searchParams.get("cppId"))
        : 0;
      if (isEdit) {
        editBankOrCard("CC", isEdit, cppId);
      } else {
        createWalletsProfile("CC");
      }
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }
  }, [hasLoadedPaymentPermission, isBankAvailable, isCardAvailable]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 500) {
        setShowLimits(6);
      } else {
        setShowLimits(3);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getPaymentMethodPermission = async () => {
    setHasLoadedPaymentPermission(false);
    setPaymentPermissionLoading(true);
    try {
      let payload = {
        accountid: userData?.data?.accountData?.accountid,
        clusterid: clusterId,
        lead_id: accountLeadData?.lead_id,
      };
      const response = await dispatch(getpaymentPermission(payload)).unwrap();
      setHasLoadedPaymentPermission(
        response?.status === 200 || response?.status === 201
      );
    } catch (error) {
      console.log("getPaymentMethodPermission error =====", error);
      setHasLoadedPaymentPermission(false);
    } finally {
      setPaymentPermissionLoading(false);
    }
  };

  useEffect(() => {
    if (isLogin) {
      getPaymentMethodPermission();
    } else {
      setHasLoadedPaymentPermission(false);
      setPaymentPermissionLoading(false);
    }
  }, [isLogin]);

  useEffect(() => {
    if (!hasLoadedPaymentPermission) return;

    if (!isCardAvailable) {
      setIsAddCard(false);
      setIsEditCard(false);
      setEditCardData(null);
    }

    if (!isBankAvailable) {
      setIsAddBank(false);
      setIsEditBank(false);
      setEditBankData(null);
    }
  }, [hasLoadedPaymentPermission, isCardAvailable, isBankAvailable]);

  const handleBadgeDisplay = (show?: boolean) => {
    const badge = document.querySelector(".grecaptcha-badge");
    if (badge && badge instanceof HTMLElement) {
      badge.style.display = show ? "block" : "none";
    }
  };

  useEffect(() => {
    if (isAddCard || isAddBank) {
      handleBadgeDisplay(true);
    } else {
      handleBadgeDisplay(false);
    }
  }, [isAddCard, isAddBank]);

  const isPaymentMethodsLoading = isLoading || isPaymentPermissionLoading;
  const isWalletsAvailable =
    hasLoadedPaymentPermission &&
    (paymentPermission?.pp_ext_availability === "Y" ||
      paymentPermission?.capp_ext_availability === "Y" ||
      paymentPermission?.amzp_ext_availability === "Y" ||
      paymentPermission?.ap_ext_availability === "Y" ||
      paymentPermission?.gp_ext_availability === "Y");

  return (
    <div
      className="w-full lg:h-full lg:block flex flex-col lg:overflow-hidden"
      data-testid={tid("page")}
    >
      <Header
        headerContent={
          <div className="w-full h-fit lg:text-[32px] sm:text-3xl text-2xl leading-[44px] text-txtPrimary font-medium -tracking-[0.72px] lg:p-0 p-5">
            Payment Methods
          </div>
        }
      />
      <div
        className="lg:pt-[93px] h-full lg:overflow-y-auto "
        data-testid={tid("content")}
        aria-busy={isPaymentMethodsLoading}
      >
        <div className="lg:px-10 px-5 lg:pt-10 h-full flex flex-col lg:gap-20 gap-[30px] lg:overflow-y-scroll scroll-smooth">
          {isPaymentMethodsLoading && (
            <span
              className="sr-only"
              role="status"
              data-testid={tid("loading-status")}
            >
              Loading payment methods
            </span>
          )}
          {(isPaymentPermissionLoading || isCardAvailable) && (
            <div data-testid={tid("cards-section")}>
              <div className=" flex items-center justify-between">
                <span className="lg:text-2xl text-xl font-medium leading-normal text-txtPrimary">
                  Cards
                </span>
                {!isPaymentMethodsLoading && (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Link a new card"
                    data-testid={tid("cards-mobile-add-button")}
                    className="lg:hidden text-blue hover:!text-btnPrimaryHover cursor-pointer text-sm flex gap-1 justify-center items-center"
                    onClick={() => {
                      setIsAddCard(true);
                      setIsEditCard(false);
                    }}
                  >
                    <AddCirclePrimaryIcon width={24} height={24} />
                    New Card
                  </span>
                )}
              </div>

              {/* DESKTOP LAPTOP VIEW */}
              <div className="pt-10 lg:block hidden">
                <div className="flex items-center xl:gap-[30px] gap-5 flex-wrap">
                  {isPaymentMethodsLoading ? (
                    <>
                      {[0, 1, 2, 3, 4]?.map((x: any, id: number) => (
                        <div key={id}>
                          <Skeleton width={tileW} height={tileH} />
                        </div>
                      ))}
                      {[0, 1, 2, 3, 4]?.map((x: any, id: number) => (
                        <div key={id}>
                          <Skeleton width={tileW} height={tileH} />
                        </div>
                      ))}
                    </>
                  ) : (
                    oldCardData?.map((val: any, idx: number) => (
                      <PaymentMethodCard
                        key={getPaymentMethodKey(val, idx)}
                        val={val}
                        idx={idx}
                        onDelete={() =>
                          setDeleteCard({
                            number: val?.cc_4digit,
                            id: val?.id,
                            showModal: true,
                          })
                        }
                        onEdit={() => openEditCard(val?.id)}
                        onDefault={() =>
                          setCardNumber({
                            number: val?.cc_4digit,
                            id: val?.id,
                            showModal: true,
                          })
                        }
                        isMobile={false}
                      />
                    ))
                  )}
                  {!isPaymentMethodsLoading && (
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label="Link a new card"
                      data-testid={tid("cards-desktop-add-button")}
                      className="border-strokePrimary border-dashed border rounded-[16px] h-[200px] hover:opacity-70 w-[350px] max-w-full xl:rounded-[12px] xl:h-[150px] xl:w-[260px] p-4 flex justify-center items-center flex-col gap-5 text-lg font-medium text-blue cursor-pointer"
                      onClick={() => setIsAddCard(true)}
                    >
                      <AddCirclePrimaryIcon />
                      Link a new card
                    </div>
                  )}
                </div>
              </div>

              {/* MOBILE TABLET VIEW */}
              <div className="lg:hidden grid place-items-center pt-5">
                <div className="flex justify-center min-[770px]:justify-start gap-[30px] flex-wrap w-fit max-w-[730px]">
                  {isPaymentMethodsLoading ? (
                    <>
                      {Array.from({ length: showLimits })?.map(
                        (x: any, id: number) => (
                          <div key={id}>
                            <Skeleton width={tileW} height={tileH} />
                          </div>
                        )
                      )}
                    </>
                  ) : (
                    oldCardData
                      ?.slice(0, limitCards)
                      .map((val: any, idx: number) => (
                        <PaymentMethodCard
                          key={getPaymentMethodKey(val, idx)}
                          val={val}
                          idx={idx}
                          onDelete={() =>
                            setDeleteCard({
                              number: val?.cc_4digit,
                              id: val?.id,
                              showModal: true,
                            })
                          }
                          onEdit={() => openEditCard(val?.id)}
                          onDefault={() =>
                            setCardNumber({
                              number: val?.cc_4digit,
                              id: val?.id,
                              showModal: true,
                            })
                          }
                          isMobile={true}
                        />
                      ))
                  )}
                </div>

                {!isPaymentMethodsLoading &&
                  oldCardData.length > showLimits && (
                    <div className="pt-5 w-full text-center">
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label={
                          limitCards < oldCardData.length
                            ? "Show more cards"
                            : "Show fewer cards"
                        }
                        data-testid={tid("cards-show-more-button")}
                        className="cursor-pointer text-blue hover:!text-btnPrimaryHover underline"
                        onClick={() => {
                          if (limitCards < oldCardData.length) {
                            setLimitCards(limitCards + showLimits);
                          } else {
                            setLimitCards(showLimits);
                          }
                        }}
                      >
                        {limitCards < oldCardData.length
                          ? "Show more"
                          : "Show less"}
                      </span>
                    </div>
                  )}
              </div>
            </div>
          )}

          {(isPaymentPermissionLoading || isBankAvailable) && (
            <div data-testid={tid("banks-section")}>
              <div className="flex items-center justify-between">
                <span className="lg:text-2xl text-xl font-medium leading-normal text-txtPrimary">
                  Banks
                </span>
                {!isPaymentMethodsLoading && (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Link a new bank"
                    data-testid={tid("banks-mobile-add-button")}
                    className="lg:hidden text-blue hover:!text-btnPrimaryHover cursor-pointer text-sm flex gap-1 justify-center items-center"
                    onClick={() => {
                      setIsAddBank(true);
                      setIsEditBank(false);
                    }}
                  >
                    <AddCirclePrimaryIcon width={24} height={24} />
                    New Bank
                  </span>
                )}
              </div>

              {/* DESKTOP AND LAPTOP VIEW */}
              <div className="pt-10 lg:block hidden">
                <div className="flex items-center xl:gap-[30px] gap-5 flex-wrap">
                  {isPaymentMethodsLoading ? (
                    <>
                      {[0, 1, 2, 3, 4]?.map((x: any, id: number) => {
                        return (
                          <div key={id}>
                            <Skeleton width={tileW} height={tileH} />
                          </div>
                        );
                      })}
                      {[0, 1, 2, 3, 4]?.map((x: any, id: number) => {
                        return (
                          <div key={id}>
                            <Skeleton width={tileW} height={tileH} />
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    oldBankData?.map((val: any, idx: number) => (
                      <PaymentMethodBankCard
                        key={getPaymentMethodKey(val, idx)}
                        val={val}
                        idx={idx}
                        onDelete={() =>
                          setDeleteCard({
                            number: maskString(val?.account_number),
                            id: val?.id,
                            showModal: true,
                          })
                        }
                        onReconnect={() => {
                          setIsAddBank(true);
                          setIsEditBank(true);
                          setEditBankData(val);
                        }}
                        onPlaidReconnect={onOldCardDataGet}
                        onDefault={() =>
                          setCardNumber({
                            number: maskString(val?.account_number),
                            id: val?.id,
                            showModal: true,
                          })
                        }
                        isMobile={false}
                      />
                    ))
                  )}
                  {!isPaymentMethodsLoading && (
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label="Link a new bank"
                      data-testid={tid("banks-desktop-add-button")}
                      className="border-strokePrimary border-dashed border rounded-[16px] h-[200px] hover:opacity-70 w-[350px] max-w-full xl:rounded-[12px] xl:h-[150px] xl:w-[260px] p-4 flex justify-center items-center flex-col gap-5 text-lg font-medium text-blue cursor-pointer"
                      onClick={() => setIsAddBank(true)}
                    >
                      <AddCirclePrimaryIcon />
                      Link a new bank
                    </div>
                  )}
                </div>
              </div>

              {/* MOBILE AND TALET VIEW */}
              <div className="lg:hidden grid place-items-center pt-5">
                <div className="flex justify-center min-[770px]:justify-start gap-[30px] flex-wrap w-fit max-w-[730px]">
                  {isPaymentMethodsLoading ? (
                    <>
                      {Array.from({ length: showLimits })?.map(
                        (x: any, id: number) => {
                          return (
                            <div key={id}>
                              <Skeleton width={tileW} height={tileH} />
                            </div>
                          );
                        }
                      )}
                    </>
                  ) : (
                    oldBankData
                      ?.slice(0, limitBanks)
                      ?.map((val: any, idx: number) => (
                        <PaymentMethodBankCard
                          key={getPaymentMethodKey(val, idx)}
                          val={val}
                          idx={idx}
                          onDelete={() =>
                            setDeleteCard({
                              number: maskString(val?.account_number),
                              id: val?.id,
                              showModal: true,
                            })
                          }
                          onReconnect={() => {
                            setIsAddBank(true);
                            setIsEditBank(true);
                            setEditBankData(val);
                          }}
                          onPlaidReconnect={onOldCardDataGet}
                          onDefault={() =>
                            setCardNumber({
                              number: maskString(val?.account_number),
                              id: val?.id,
                              showModal: true,
                            })
                          }
                          isMobile={true}
                        />
                      ))
                  )}
                </div>
                {!isPaymentMethodsLoading &&
                  oldBankData.length > showLimits && (
                    <div className="pt-5 w-full text-center">
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label={
                          limitBanks < oldBankData.length
                            ? "Show more banks"
                            : "Show fewer banks"
                        }
                        data-testid={tid("banks-show-more-button")}
                        className="cursor-pointer text-blue hover:!text-btnPrimaryHover underline"
                        onClick={() => {
                          if (limitBanks < oldBankData.length) {
                            setLimitBanks(limitBanks + showLimits);
                          } else {
                            setLimitBanks(showLimits);
                          }
                        }}
                      >
                        {limitBanks < oldBankData.length
                          ? "Show more"
                          : "Show less"}
                      </span>
                    </div>
                  )}
              </div>
            </div>
          )}

          {(isPaymentMethodsLoading ||
            walletList.length > 0 ||
            isWalletsAvailable) && (
            <div className="" data-testid={tid("wallets-section")}>
              <div className="flex items-center justify-between">
                <span className="lg:text-2xl text-xl font-medium leading-normal text-txtPrimary">
                  Digital Wallets
                </span>
                {!isPaymentMethodsLoading && isWalletsAvailable && (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Link a new wallet"
                    data-testid={tid("wallets-mobile-add-button")}
                    className="lg:hidden text-blue hover:!text-btnPrimaryHover cursor-pointer text-sm flex gap-1 justify-center items-center"
                    onClick={() => {
                      setIsAddWallet(true);
                      setIsEditWallet(false);
                    }}
                  >
                    <AddCirclePrimaryIcon width={24} height={24} />
                    New Wallet
                  </span>
                )}
              </div>

              {/* Desktop and laptop view */}
              <div className="pt-10 pb-12 lg:block hidden">
                <div className="flex items-center xl:gap-[30px] gap-5 flex-wrap">
                  {isPaymentMethodsLoading ? (
                    <>
                      {[0, 1, 2, 3, 4]?.map((x: any, id: number) => {
                        return (
                          <div key={id}>
                            <Skeleton width={tileW} height={tileH} />
                          </div>
                        );
                      })}
                      {[0, 1, 2, 3, 4]?.map((x: any, id: number) => {
                        return (
                          <div key={id}>
                            <Skeleton width={tileW} height={tileH} />
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      {walletList?.map((val: any, idx: number) => (
                        <PaymentMethodWalletCard
                          key={getPaymentMethodKey(val, idx)}
                          val={val}
                          idx={idx}
                          onDelete={() =>
                            setWallet({
                              method: shortToCardName(val.payment_method),
                              id: val.id,
                              showModal: true,
                              type: "delete",
                            })
                          }
                          onDefault={() =>
                            setWallet({
                              method: shortToCardName(val.payment_method),
                              id: val.id,
                              showModal: true,
                              type: "default",
                            })
                          }
                          onEdit={() => {
                            setEditWalletData(val);
                            setIsEditWallet(true);
                            setIsAddWallet(true);
                          }}
                          isMobile={false}
                        />
                      ))}
                    </>
                  )}
                  {!isPaymentMethodsLoading && isWalletsAvailable && (
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label="Link a new wallet"
                      data-testid={tid("wallets-desktop-add-button")}
                      className="border-strokePrimary border-dashed border rounded-[16px] h-[200px] hover:opacity-70 w-[350px] max-w-full xl:rounded-[12px] xl:h-[150px] xl:w-[260px] p-4 flex justify-center items-center flex-col gap-5 text-lg font-medium text-blue cursor-pointer"
                      onClick={() => {
                        setEditWalletData(null);
                        setIsEditWallet(false);
                        setIsAddWallet(true);
                      }}
                    >
                      <AddCirclePrimaryIcon />
                      Link a new wallet
                    </div>
                  )}
                </div>
              </div>

              {/* tablet nad mobile view */}
              {/* Wallets are the last section, so this pb keeps the final tile
                  off the bottom edge — desktop gets the same from pb-12 above. */}
              <div className="lg:hidden grid place-items-center pt-5 pb-10">
                <div className="flex justify-center min-[770px]:justify-start gap-[30px] flex-wrap w-fit max-w-[730px]">
                  {isPaymentMethodsLoading ? (
                    <>
                      {Array.from({ length: showLimits })?.map(
                        (x: any, id: number) => {
                          return (
                            <div key={id}>
                              <Skeleton width={tileW} height={tileH} />
                            </div>
                          );
                        }
                      )}
                    </>
                  ) : (
                    <>
                      {walletList
                        ?.slice(0, limitWallets)
                        ?.map((val: any, idx: number) => (
                          <PaymentMethodWalletCard
                            key={getPaymentMethodKey(val, idx)}
                            val={val}
                            idx={idx}
                            onDelete={() =>
                              setWallet({
                                method: shortToCardName(val.payment_method),
                                id: val.id,
                                showModal: true,
                                type: "delete",
                              })
                            }
                            onDefault={() =>
                              setWallet({
                                method: shortToCardName(val.payment_method),
                                id: val.id,
                                showModal: true,
                                type: "default",
                              })
                            }
                            onEdit={() => {
                              setEditWalletData(val);
                              setIsEditWallet(true);
                              setIsAddWallet(true);
                            }}
                            isMobile={true}
                          />
                        ))}
                    </>
                  )}
                </div>
                {walletList.length > showLimits && (
                  <div className="pt-5 w-full text-center">
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={
                        limitWallets < walletList.length
                          ? "Show more wallets"
                          : "Show fewer wallets"
                      }
                      data-testid={tid("wallets-show-more-button")}
                      className="cursor-pointer text-blue hover:!text-btnPrimaryHover underline"
                      onClick={() => {
                        if (limitWallets < walletList.length) {
                          setLimitWallets(limitWallets + showLimits);
                        } else {
                          setLimitWallets(showLimits);
                        }
                      }}
                    >
                      {limitWallets < walletList.length
                        ? "Show more"
                        : "Show less"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <PaymentMethodDialogs
        isBtnLoading={isBtnLoading}
        wallet={wallet}
        setWallet={setWallet}
        handleUpdateWallet={handleUpdateWallet}
        cardNumber={cardNumber}
        setCardNumber={setCardNumber}
        deleteCard={deleteCard}
        setDeleteCard={setDeleteCard}
        onCardDefault={onCardDefault}
        onCardDelete={onCardDelete}
        isLessThan590={isLessThan590}
        isCardAvailable={isCardAvailable}
        isAddCard={isAddCard}
        setIsAddCard={setIsAddCard}
        isEditCard={isEditCard}
        setIsEditCard={setIsEditCard}
        editCardData={editCardData}
        setEditCardData={setEditCardData}
        onOldCardDataGet={onOldCardDataGet}
        isBankAvailable={isBankAvailable}
        isAddBank={isAddBank}
        setIsAddBank={setIsAddBank}
        isEditBank={isEditBank}
        setIsEditBank={setIsEditBank}
        editBankData={editBankData}
        setEditBankData={setEditBankData}
        isAddWallet={isAddWallet}
        setIsAddWallet={setIsAddWallet}
        isEditWallet={isEditWallet}
        setIsEditWallet={setIsEditWallet}
        editWalletData={editWalletData}
        setEditWalletData={setEditWalletData}
        onOldWalletDataGet={onOldCardDataGet}
        isSuccessScreeenWallet={isSuccessScreeenWallet}
        isFailedScreenWallet={isFailedScreenWallet}
        isScreenLoading={isScreenLoading}
      />
    </div>
  );
};

const PaymentMethod: React.FC = () => (
  <TestIdScope scope="payment-methods">
    <PaymentMethodContent />
  </TestIdScope>
);

export default PaymentMethod;
